import { useMemo, useState } from "react";
import { X } from "lucide-react";
import { useComponentsQuery } from "../../hooks/queries/useComponentsQuery";

type NotificationAcf = {
  bericht?: string;
  description?: string;
  visible_from?: string;
  visible_to?: string;
};

type Notification = {
  id: number;
  acf?: NotificationAcf;
  title?: { rendered?: string };
};

const DISMISSED_KEY = "lp:dismissed-notifications";

const parseDate = (value?: string): Date | null => {
  if (!value) return null;
  if (value.includes("/")) {
    const [d, m, y] = value.split("/");
    if (d && m && y) return new Date(`${y}-${m}-${d}`);
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const stripHtml = (value?: string) => (value ?? "").replace(/<[^>]+>/g, "").trim();

const readDismissed = (): number[] => {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    return raw ? (JSON.parse(raw) as number[]) : [];
  } catch {
    return [];
  }
};

export function NotificationBar() {
  const { data } = useComponentsQuery();
  const notifications = (data?.notifications ?? []) as Notification[];

  const [dismissed, setDismissed] = useState<number[]>(() =>
    typeof window === "undefined" ? [] : readDismissed(),
  );

  const active = useMemo(() => {
    const now = new Date();
    return notifications.filter((n) => {
      if (dismissed.includes(n.id)) return false;
      const from = parseDate(n.acf?.visible_from);
      const to = parseDate(n.acf?.visible_to);
      if (from && now < from) return false;
      if (to && now > to) return false;
      const text = stripHtml(n.acf?.bericht ?? n.acf?.description ?? n.title?.rendered);
      return text.length > 0;
    });
  }, [notifications, dismissed]);

  const setRef = (el: HTMLElement | null) => {
    if (!el) {
      document.documentElement.style.setProperty("--notification-bar-h", "0px");
      return;
    }
    const update = () => {
      document.documentElement.style.setProperty(
        "--notification-bar-h",
        `${el.offsetHeight}px`,
      );
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
  };

  const dismiss = (id: number) => {
    const next = [...dismissed, id];
    setDismissed(next);
    try {
      localStorage.setItem(DISMISSED_KEY, JSON.stringify(next));
    } catch {
      // ignore
    }
  };

  if (active.length === 0) return null;

  return (
    <section
      ref={setRef}
      className="fixed inset-x-0 top-0 z-[60] bg-accent text-[#1f1a12] shadow-sm"
      aria-label="Mededelingen"
    >
      {active.map((n) => {
        const text = stripHtml(n.acf?.bericht ?? n.acf?.description ?? n.title?.rendered);
        return (
          <div
            key={n.id}
            className="mx-auto flex w-full max-w-7xl items-center gap-3 px-4 py-2 sm:px-6 lg:px-8"
          >
            <p className="flex-1 text-center font-display text-sm font-light tracking-wide">
              {text}
            </p>
            <button
              type="button"
              onClick={() => dismiss(n.id)}
              aria-label="Sluit mededeling"
              className="shrink-0 rounded-full p-1 text-[#1f1a12]/70 transition-colors hover:bg-black/10 hover:text-[#1f1a12]"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        );
      })}
    </section>
  );
}
